import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardDescription } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { Download, PackageCheck, ShoppingCart } from "lucide-react"
import { useTranslation } from "react-i18next"

type FlowItem = {
  id: string
  fileName: string
  titleKey: string
  descriptionKey: string
  icon: typeof PackageCheck
}

const flows: FlowItem[] = [
  {
    id: "order-fulfilled",
    fileName: "order-fulfilled.flow",
    titleKey: "settings.integrations.shopify.flows.orderFulfilled.title",
    descriptionKey: "settings.integrations.shopify.flows.orderFulfilled.description",
    icon: PackageCheck,
  },
  {
    id: "customer-abandons-checkout",
    fileName: "customer-abandons-checkout.flow",
    titleKey: "settings.integrations.shopify.flows.customerAbandonsCheckout.title",
    descriptionKey: "settings.integrations.shopify.flows.customerAbandonsCheckout.description",
    icon: ShoppingCart,
  },
]

const downloadFlow = (fileName: string) => {
  const url = `/shopify-flows/${fileName}`
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

type ShopifyFlowsProps = {
  isDisabled?: boolean
  disabledReason?: string
}

export const ShopifyFlows = ({ isDisabled, disabledReason }: ShopifyFlowsProps) => {
  const { t } = useTranslation()

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {flows.map((flow) => {
        const Icon = flow.icon
        return (
          <Tooltip key={flow.id}>
            <TooltipTrigger asChild>
              <Card
                className={cn("flex flex-col gap-3 p-4", isDisabled && "opacity-50")}
                aria-disabled={isDisabled}
              >
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row gap-2.5 items-center">
                    <Icon size={24} />
                    <div className="text-lg font-medium">{t(flow.titleKey)}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (!isDisabled) downloadFlow(flow.fileName)
                    }}
                    className="ml-auto shrink-0"
                    disabled={isDisabled}
                    title={t("settings.integrations.shopify.flows.download")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>{t(flow.descriptionKey)}</CardDescription>
              </Card>
            </TooltipTrigger>
            {isDisabled && disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
          </Tooltip>
        )
      })}
    </div>
  )
}
